import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '..'
import {
  ILearningPathConfiguration,
  ILearningPathContent,
  ILearningPathState,
  IChangeRequired,
  IChangeLearningPath,
  ILearningPath,
  IAddCourse,
  IRemoveCourse,
  IChangeCollectionName,
  IAddCoursePrerequisites,
  ICollection,
  IUpdateRelatedCoursesOrder,
} from 'interfaces/learningPath'
import {
  initialLearningPathContent,
  initialLearningPathConfiguration,
} from './initialData'
import { CourseLmsProfileType } from 'enum/course-lms-profile-type.enum'

const initialState: ILearningPathState = {
  items: [],
  loading: false,
  loaded: false,
  loadingConfiguration: false,
  configuration: initialLearningPathConfiguration,
  loadingContent: false,
  content: initialLearningPathContent,
  configurationUpdated: false,
  contentUpdated: false,
}

const learningPathSlice = createSlice({
  name: 'learningPath',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setItems: (state, action: PayloadAction<ILearningPath[]>) => {
      state.items = action.payload
      state.loaded = true
    },
    updateItem: (state, action: PayloadAction<ILearningPath>) => {
      let hasBeenUpdated = false
      const updatedLearningPaths = state.items.map((item) => {
        if (item.id === action.payload.id) {
          hasBeenUpdated = true
          return action.payload
        }
        return item
      })
      if (hasBeenUpdated) {
        state.items = updatedLearningPaths
      } else {
        state.items = [...updatedLearningPaths, action.payload]
      }
    },
    setLoadingConfiguration: (state, action: PayloadAction<boolean>) => {
      state.loadingConfiguration = action.payload
    },
    setConfiguration: (
      state,
      action: PayloadAction<ILearningPathConfiguration>
    ) => {
      state.configuration = action.payload
      state.configurationUpdated = false
    },
    setLoadingContent: (state, action: PayloadAction<boolean>) => {
      state.loadingContent = action.payload
    },
    setContent: (state, action: PayloadAction<ILearningPathContent>) => {
      state.content = action.payload
      state.contentUpdated = false
    },
    setContentUpdated: (state, action: PayloadAction<boolean>) => {
      state.contentUpdated = action.payload
    },
    setName: (state, action: PayloadAction<string>) => {
      state.configurationUpdated = true
      state.configuration.name = action.payload
    },
    setDescription: (state, action: PayloadAction<string>) => {
      state.configurationUpdated = true
      state.configuration.description = action.payload
    },
    setImage: (state, action: PayloadAction<any>) => {
      state.configurationUpdated = true
      state.configuration.image = action.payload
    },
    setProfiles: (state, action: PayloadAction<number[]>) => {
      state.configurationUpdated = true
      state.configuration.profiles = action.payload
    },
    setState: (state, action: PayloadAction<number>) => {
      state.configurationUpdated = true
      state.configuration.state = action.payload
    },
    changeObligation: (state, action: PayloadAction<IChangeRequired>) => {
      state.contentUpdated = true
      state.content.collections.forEach((collection) => {
        if (collection.order === action.payload.collectionOrder) {
          collection.relatedCourses.forEach((relatedCourse) => {
            if (relatedCourse.order === action.payload.courseOrder) {
              relatedCourse.required = action.payload.required
            }
          })
        }
      })
    },
    changeLearningPath: (state, action: PayloadAction<IChangeLearningPath>) => {
      state.contentUpdated = true
      state.content.learningPaths = state.content.learningPaths.map((item) =>
        item === action.payload.previousLearningPath
          ? action.payload.learningPath
          : item
      )
    },
    addEmptyLearningPath: (state) => {
      state.content.learningPaths = [...state.content.learningPaths, 0]
    },
    removeLearningPath: (state, action: PayloadAction<number>) => {
      state.contentUpdated = true
      const updatedLearningPath = state.content.learningPaths.filter(
        (item) => item !== action.payload
      )
      state.content.learningPaths = updatedLearningPath.length
        ? updatedLearningPath
        : [0]
    },
    removeAllLearningPath: (state) => {
      state.contentUpdated = true
      state.content.learningPaths = [0]
    },
    addCourses: (state, action: PayloadAction<IAddCourse>) => {
      state.contentUpdated = true
      state.content.collections.forEach((collection) => {
        if (collection.order === action.payload.collectionOrder) {
          const courses = action.payload.selectedCourses.map(
            (course, index) => {
              return {
                course: {
                  id: course.id,
                  name: course.name,
                  state: course.state,
                },
                prerequisites: [],
                required: CourseLmsProfileType.Required,
                order: collection.relatedCourses.length + index,
              }
            }
          )
          collection.relatedCourses = [...collection.relatedCourses, ...courses]
        }
      })
    },
    removeCourse: (state, { payload }: PayloadAction<IRemoveCourse>) => {
      state.contentUpdated = true
      state.content.collections.forEach((collection) => {
        if (collection.order === payload.collectionOrder) {
          const indexRemoved = payload.courseOrder
          const removed = collection.relatedCourses[indexRemoved]
          collection.relatedCourses.splice(indexRemoved, 1)
          collection.relatedCourses = collection.relatedCourses.map(
            (course, index) => {
              course.order = index
              course.prerequisites = course.prerequisites.filter(
                (prerequisite) => prerequisite.id !== removed.course.id
              )
              return course
            }
          )
        }
      })
    },
    addEmptyCollection: (state) => {
      state.contentUpdated = true
      state.content.collections = [
        ...state.content.collections,
        {
          id: 0,
          name: `Colección de cursos ${state.content.collections.length + 1}`,
          order: state.content.collections.length,
          relatedCourses: [],
        },
      ]
    },
    removeCollection: (state, action: PayloadAction<number>) => {
      state.contentUpdated = true
      let collections = state.content.collections.filter(
        (collection) => collection.order !== action.payload
      )
      if (collections.length > 0) {
        state.content.collections = collections.map((collection, index) => {
          collection.name = collection.name.includes('Colección de cursos ')
            ? `Colección de cursos ${index + 1}`
            : collection.name
          collection.order = index
          return collection
        })
      } else {
        state.content.collections = [
          {
            id: 0,
            name: 'Colección de cursos 1',
            order: 0,
            relatedCourses: [],
          },
        ]
      }
    },
    changeCollectionName: (
      state,
      action: PayloadAction<IChangeCollectionName>
    ) => {
      state.contentUpdated = true
      state.content.collections.forEach((collection) => {
        if (collection.order === action.payload.collectionOrder) {
          collection.name = action.payload.collectionName
        }
      })
    },
    validateCollectionName: (state, action: PayloadAction<number>) => {
      state.contentUpdated = true
      state.content.collections.forEach((collection, index) => {
        if (collection.order === action.payload) {
          if (collection.name.trim().length === 0) {
            collection.name = `Colección de cursos ${index + 1}`
          }
        }
      })
    },
    updateCoursePrerequisites: (
      state,
      action: PayloadAction<IAddCoursePrerequisites>
    ) => {
      state.contentUpdated = true
      for (const collection of state.content.collections) {
        for (const relatedCourse of collection.relatedCourses) {
          const courses = action.payload.selectedCourses.map(
            (course) => course.id
          )
          if (courses.includes(relatedCourse.course.id)) {
            relatedCourse.required = CourseLmsProfileType.Required
          }
          if (
            collection.order === action.payload.collectionOrder &&
            relatedCourse.order === action.payload.courseOrder
          ) {
            relatedCourse.prerequisites = action.payload.selectedCourses
            relatedCourse.required = CourseLmsProfileType.Required
          }
        }
      }
    },
    updateCollectionsOrder: (state, action: PayloadAction<ICollection[]>) => {
      state.contentUpdated = true
      state.content.collections = action.payload.map((collection, index) => {
        return {
          ...collection,
          name: collection.name.includes('Colección de cursos ')
            ? `Colección de cursos ${index + 1}`
            : collection.name,
          order: index,
        }
      })
    },
    updateRelatedCoursesOrder: (
      state,
      action: PayloadAction<IUpdateRelatedCoursesOrder>
    ) => {
      state.contentUpdated = true
      for (const collection of state.content.collections) {
        if (collection.order === action.payload.collectionOrder) {
          collection.relatedCourses = action.payload.relatedCourses.map(
            (relatedCourse, index) => {
              return {
                ...relatedCourse,
                order: index,
              }
            }
          )
          break
        }
      }
    },
  },
})

const {
  setLoading,
  setItems,
  updateItem,
  setLoadingConfiguration,
  setLoadingContent,
  setConfiguration,
  setContent,
  setContentUpdated,
  setName,
  setDescription,
  setImage,
  setProfiles,
  setState,
  changeObligation,
  changeLearningPath,
  addEmptyLearningPath,
  removeLearningPath,
  removeAllLearningPath,
  addCourses,
  removeCourse,
  addEmptyCollection,
  removeCollection,
  changeCollectionName,
  validateCollectionName,
  updateCoursePrerequisites,
  updateCollectionsOrder,
  updateRelatedCoursesOrder,
} = learningPathSlice.actions
const loading = (state: RootState) => state.learningPath.loading
const loaded = (state: RootState) => state.learningPath.loaded
const items = (state: RootState) => state.learningPath.items
const configuration = (state: RootState) => state.learningPath.configuration
const loadingConfiguration = (state: RootState) =>
  state.learningPath.loadingConfiguration
const configurationUpdated = (state: RootState) =>
  state.learningPath.configurationUpdated
const content = (state: RootState) => state.learningPath.content
const loadingContent = (state: RootState) => state.learningPath.loadingContent
const contentUpdated = (state: RootState) => state.learningPath.contentUpdated

export const learningPath = {
  setLoading,
  setItems,
  updateItem,
  setLoadingConfiguration,
  setLoadingContent,
  setConfiguration,
  setContent,
  setContentUpdated,
  setName,
  setDescription,
  setImage,
  setProfiles,
  setState,
  changeObligation,
  changeLearningPath,
  addEmptyLearningPath,
  removeLearningPath,
  removeAllLearningPath,
  addCourses,
  removeCourse,
  addEmptyCollection,
  removeCollection,
  changeCollectionName,
  validateCollectionName,
  updateCoursePrerequisites,
  updateCollectionsOrder,
  updateRelatedCoursesOrder,
  loading,
  loaded,
  items,
  configuration,
  loadingConfiguration,
  configurationUpdated,
  content,
  loadingContent,
  contentUpdated,
}

export default learningPathSlice.reducer
